import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Loader,
  Modal,
  Select,
  Stack,
  TextInput,
  Group,
  ColorInput,
  MultiSelect,
  NumberInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCube,
  IconPencil,
  IconPlus,
  IconRocket,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import {
  useCourses,
  useCreateLearningTrack,
  useDeleteLearningTrack,
  useLearningTracks,
  useUpdateLearningTrack,
} from '../../hooks/useApi';
import { Course, LearningTrack, LearningTrackIcon, LearningTrackInput } from '../../types';
import { showToast } from '../../utils/toast';

const useIconOptions = (): Array<{ value: LearningTrackIcon; label: string }> => {
  const { t } = useTranslation();
  return [
    { value: 'rocket', label: t('learning.tracksAdmin.iconRocket') },
    { value: 'users', label: t('learning.tracksAdmin.iconUsers') },
    { value: 'cube', label: t('learning.tracksAdmin.iconCube') },
  ];
};

const TrackIcon: React.FC<{ kind: LearningTrackIcon; size?: number }> = ({ kind, size = 18 }) => {
  if (kind === 'rocket') return <IconRocket size={size} />;
  if (kind === 'users') return <IconUsers size={size} />;
  return <IconCube size={size} />;
};

interface TrackFormState {
  title: string;
  icon: LearningTrackIcon;
  color: string;
  courseIds: string[];
  sortOrder: number;
}

const emptyForm = (): TrackFormState => ({
  title: '',
  icon: 'rocket',
  color: '#12B886',
  courseIds: [],
  sortOrder: 0,
});

const TrackForm: React.FC<{
  opened: boolean;
  initial: TrackFormState;
  courses: Course[];
  isLoading: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (data: LearningTrackInput) => void;
}> = ({ opened, initial, courses, isLoading, title, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const iconOptions = useIconOptions();
  const [form, setForm] = useState<TrackFormState>(initial);

  React.useEffect(() => {
    if (opened) setForm(initial);
  }, [opened, initial]);

  const courseOptions = useMemo(
    () =>
      (courses || []).map((c) => ({
        value: c._id,
        label: c.title || t('learning.tracksAdmin.courseNoTitle'),
      })),
    [courses, t],
  );

  const canSubmit = form.title.trim().length > 0;

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md" centered>
      <Stack gap="md">
        <TextInput
          label={t('learning.tracksAdmin.fieldTitle')}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.currentTarget.value })}
          required
          autoFocus
          placeholder={t('learning.tracksAdmin.fieldTitlePlaceholder')}
        />
        <Group grow>
          <Select
            label={t('learning.tracksAdmin.fieldIcon')}
            value={form.icon}
            onChange={(v) => v && setForm({ ...form, icon: v as LearningTrackIcon })}
            data={iconOptions}
            allowDeselect={false}
          />
          <ColorInput
            label={t('learning.tracksAdmin.fieldColor')}
            format="hex"
            value={form.color}
            onChange={(v) => setForm({ ...form, color: v })}
            withEyeDropper
          />
        </Group>
        <MultiSelect
          label={t('learning.tracksAdmin.fieldCourses')}
          placeholder={t('learning.tracksAdmin.fieldCoursesPlaceholder')}
          data={courseOptions}
          value={form.courseIds}
          onChange={(v) => setForm({ ...form, courseIds: v })}
          searchable
          clearable
          hidePickedOptions
        />
        <NumberInput
          label={t('learning.tracksAdmin.fieldOrder')}
          description={t('learning.tracksAdmin.fieldOrderHint')}
          value={form.sortOrder}
          onChange={(v) => setForm({ ...form, sortOrder: typeof v === 'number' ? v : 0 })}
          min={0}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!canSubmit}
            loading={isLoading}
            onClick={() =>
              onSubmit({
                title: form.title.trim(),
                icon: form.icon,
                color: form.color,
                courseIds: form.courseIds,
                sortOrder: form.sortOrder,
              })
            }
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export const LearningTracksAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: tracks = [], isLoading, error } = useLearningTracks();
  const { data: courses = [] } = useCourses();
  const createMut = useCreateLearningTrack();
  const updateMut = useUpdateLearningTrack();
  const deleteMut = useDeleteLearningTrack();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editing, setEditing] = useState<LearningTrack | null>(null);

  const handleCreate = async (data: LearningTrackInput) => {
    try {
      await createMut.mutateAsync(data);
      showToast({ title: t('learning.tracksAdmin.createSuccess'), message: '' });
      setIsCreateOpen(false);
    } catch {
      showToast({
        title: t('common.error'),
        message: t('learning.tracksAdmin.createError'),
        type: 'error',
      });
    }
  };

  const handleUpdate = async (data: LearningTrackInput) => {
    if (!editing) return;
    try {
      await updateMut.mutateAsync({ id: editing._id, data });
      showToast({ title: t('learning.tracksAdmin.updateSuccess'), message: '' });
      setEditing(null);
    } catch {
      showToast({
        title: t('common.error'),
        message: t('learning.tracksAdmin.updateError'),
        type: 'error',
      });
    }
  };

  const handleDelete = (track: LearningTrack) =>
    modals.openConfirmModal({
      title: t('learning.tracksAdmin.deleteTitle'),
      children: <span>{t('learning.tracksAdmin.deleteMessage', { title: track.title })}</span>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMut.mutateAsync(track._id);
          showToast({ title: t('learning.tracksAdmin.deleteSuccess'), message: '' });
        } catch {
          showToast({
            title: t('common.error'),
            message: t('learning.tracksAdmin.deleteError'),
            type: 'error',
          });
        }
      },
    });

  return (
    <div className="learning-tracks-admin">
      <style>{`
        .learning-tracks-admin {
          --lta-bg: var(--mantine-color-body);
          --lta-surface: var(--mantine-color-body);
          --lta-surface-2: var(--mantine-color-default-hover);
          --lta-line: var(--mantine-color-default-border);
          --lta-ink: var(--mantine-color-text);
          --lta-muted: var(--mantine-color-dimmed);
          --lta-accent: var(--mantine-primary-color-filled);
          color: var(--lta-ink);
          padding: 32px 40px 60px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .lta-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .lta-back { background: none; border: none; color: var(--lta-muted); cursor: pointer; padding: 0; font: inherit; display: inline-flex; align-items: center; gap: 4px; font-size: 13px; }
        .lta-back:hover { color: var(--lta-ink); }
        .lta-title { font-size: 26px; font-weight: 700; letter-spacing: -0.01em; margin: 0; color: var(--lta-ink); }
        .lta-subtitle { font-size: 13px; color: var(--lta-muted); margin-top: 4px; }
        .lta-empty { padding: 48px; text-align: center; color: var(--lta-muted); font-size: 14px; background: var(--lta-surface); border: 1px dashed var(--lta-line); border-radius: 12px; }
        .lta-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
        .lta-card { background: var(--lta-surface); border: 1px solid var(--lta-line); border-radius: 12px; padding: 18px; display: flex; flex-direction: column; gap: 12px; }
        .lta-card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .lta-card-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lta-card-actions { display: flex; gap: 4px; }
        .lta-card-action { width: 28px; height: 28px; border-radius: 6px; border: none; background: transparent; color: var(--lta-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .lta-card-action:hover { background: var(--lta-surface-2); color: var(--lta-ink); }
        .lta-card-action-danger:hover { color: #E03131; }
        .lta-card-title { font-size: 15px; font-weight: 600; margin: 0; }
        .lta-card-meta { font-size: 12px; color: var(--lta-muted); display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .lta-num { font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="lta-bar">
        <div>
          <button type="button" className="lta-back" onClick={() => navigate('/learning')}>
            <IconArrowLeft size={14} /> {t('learning.tracksAdmin.back')}
          </button>
          <h1 className="lta-title" style={{ marginTop: 8 }}>
            {t('learning.tracksAdmin.title')}
          </h1>
          <div className="lta-subtitle">{t('learning.tracksAdmin.subtitle')}</div>
        </div>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setIsCreateOpen(true)}>
          {t('learning.tracksAdmin.createBtn')}
        </Button>
      </div>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
          {t('learning.tracksAdmin.loadError')}
        </Alert>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader size="sm" />
        </div>
      ) : tracks.length === 0 ? (
        <div className="lta-empty">{t('learning.tracksAdmin.empty')}</div>
      ) : (
        <div className="lta-grid">
          {tracks.map((track) => (
            <div key={track._id} className="lta-card">
              <div className="lta-card-head">
                <div
                  className="lta-card-icon"
                  style={{ background: `${track.color}22`, color: track.color }}
                >
                  <TrackIcon kind={track.icon} />
                </div>
                <div className="lta-card-actions">
                  <button
                    type="button"
                    className="lta-card-action"
                    title={t('common.edit')}
                    onClick={() => setEditing(track)}
                  >
                    <IconPencil size={14} />
                  </button>
                  <button
                    type="button"
                    className="lta-card-action lta-card-action-danger"
                    title={t('common.delete')}
                    onClick={() => handleDelete(track)}
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>
              <h3 className="lta-card-title">{track.title}</h3>
              <div className="lta-card-meta">
                <span>
                  <span className="lta-num">{track.courses}</span>{' '}
                  {t('learning.tracksAdmin.metaCoursesSuffix')}
                </span>
                <span>·</span>
                <span>
                  {t('learning.tracksAdmin.metaCompleted')}{' '}
                  <span className="lta-num">{track.completed}</span>
                </span>
                <span>·</span>
                <span>
                  {t('learning.tracksAdmin.metaSortOrder')}{' '}
                  <span className="lta-num">{track.sortOrder}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <TrackForm
        opened={isCreateOpen}
        title={t('learning.tracksAdmin.newTrack')}
        initial={emptyForm()}
        courses={courses}
        isLoading={createMut.isPending}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <TrackForm
        opened={!!editing}
        title={t('learning.tracksAdmin.editTrack')}
        initial={
          editing
            ? {
                title: editing.title,
                icon: editing.icon,
                color: editing.color,
                courseIds: editing.courseIds || [],
                sortOrder: editing.sortOrder ?? 0,
              }
            : emptyForm()
        }
        courses={courses}
        isLoading={updateMut.isPending}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdate}
      />
    </div>
  );
};
